import { Address } from '../../utils/types'
import { Swap } from '../swaps/Swap'
import { IWithdrawal } from '../withdrawals/Withdrawals'
import { IDeposit } from '../deposits/Deposit'

type Nonce = number
export type Hash = string
export type Timestamp = number

interface UIRef {
  current: boolean
}

export interface Transaction {
  nonce: Nonce
  type:
    | 'swap'
    | 'deposit-multi'
    | 'deposit-one'
    | 'withdraw-multi'
    | 'withdraw-one'

  account: Address
  chainId: number
  uiRef?: UIRef
  data: any
  status: 'pending' | 'success' | 'failed'
  submitted: {
    hash: Hash
    // computer time, not BC time
    start: Timestamp
    speedUp: boolean
  }
  swap?: Swap | any // TODO: fix type
  withdrawal?: IWithdrawal
  deposit?: IDeposit
}
export interface SubmittedTransaction extends Transaction {
  status: 'pending'
}
export interface SuccessfulTransaction extends Transaction {
  status: 'success'
  result: {
    // computer time
    end: Timestamp
    // BC time
    blockNumber: number
  }
}

export interface FailedTransaction extends Transaction {
  status: 'failed'
  result: {
    // computer time
    end: Timestamp
  }
}

export interface TransactionsState {
  loaded: boolean
  chainId?: number
  account?: string
  all: Transaction[]
  pending: SubmittedTransaction[]
  completed: SuccessfulTransaction[]
  failed: FailedTransaction[]
  pops: Transaction[]
}

export interface TransactionAction {
  type:
    | 'load'
    | 'submit'
    | 'clear'
    | 'complete'
    | 'fail'
    | 'change'
    | 'pop'
    | 'unpop'
  tx?: Transaction
  hash?: Hash
  data?: any
}

export const INITIAL_TRANSACTION_STATE = (
  account?: string,
  chainId?: number
): TransactionsState => ({
  loaded: false,
  all: [],
  pending: [],
  completed: [],
  failed: [],
  account,
  chainId,
  pops: [],
})

function updateState(currentState: TransactionsState): TransactionsState {
  let all = currentState.all.sort(
    (x, y) => -x.submitted.start + y.submitted.start
  )
  // filter by chain and account
  const byAccount = all.filter(
    tx =>
      tx.chainId === currentState.chainId && tx.account === currentState.account
  )
  return {
    ...currentState,
    all: all, // all should always be really all
    pending: byAccount.filter(
      tx => tx.status === 'pending'
    ) as SubmittedTransaction[],
    completed: byAccount.filter(
      tx => tx.status === 'success'
    ) as SuccessfulTransaction[],
    failed: byAccount.filter(
      tx => tx.status === 'failed'
    ) as FailedTransaction[],
  }
}

function nextNonce(all: Transaction[]) {
  const nonce = all.reduce(
    (max, next) => (next.nonce > max ? next.nonce : max),
    0
  )
  return nonce + 1
}
/**
 * Predicate safe for Swaap: There are not dozen transactions at the same time, so it's very safe
 * to always load and save transactions
 */
function loadTransactionsFromLocalStorage(): Transaction[] {
  const storage = window.localStorage.getItem('transactions')
  if (storage) {
    return JSON.parse(storage, reviver) as Transaction[]
  } else {
    return []
  }
}

function reviver(key: any, value: any) {
  if (typeof value === 'object' && value !== null) {
    if (value.dataType === 'Map') {
      return new Map(value.value.map((v: any[]) => [v[0], v[1]]))
    }
  }
  return value
}

function replacer(key: any, value: any) {
  if (value instanceof Map) {
    return {
      dataType: 'Map',
      value: Array.from(value.entries()), // or with spread: value: [...value]
    }
  } else {
    return value
  }
}

function removePendingTransactions(all: Transaction[]): Transaction[] {
  return all.filter(tx => tx.status !== 'pending')
}

function findByHash(all: Transaction[], hash: string): Transaction | undefined {
  return all.find(t => t.submitted.hash === hash)
}

function saveStorage(txs: Transaction[]): void {
  window.localStorage.setItem('transactions', JSON.stringify(txs, replacer))
}
function clearStorage(): void {
  window.localStorage.removeItem('transactions')
}

function addTransaction(all: Transaction[], tx: Transaction): Transaction[] {
  if (all.find(t => t.submitted.hash === tx.submitted.hash)) {
    console.error('found the tx already', tx.type + ' ' + tx.submitted.hash)
    return all
  }

  tx.nonce = nextNonce(all)
  all.push(tx)
  saveStorage(all)
  return all
}

function displayAll(all: Transaction[]) {
  return (
    all.length +
    '\n' +
    all
      .map(tx => `${tx.type} ${tx.status} ${tx.submitted.hash.slice(0, 7)}`)
      .join('\n')
  )
}

export function transactionsReducer(
  state: TransactionsState,
  action: TransactionAction
): TransactionsState {
  switch (action.type) {
    case 'load': {
      if (!action.data) {
        console.error('No data associated to action ' + action.hash)
        return state
      }
      state = INITIAL_TRANSACTION_STATE(
        action.data.account,
        action.data.chainId
      )
      let all = loadTransactionsFromLocalStorage()
      state.loaded = true
      // TODO: delete those that are older than x,y,z
      // TODO : limit to 100 or 1000 transactions
      // TODO : decide if it's the good way
      all = removePendingTransactions(all)
      saveStorage(all)
      state.all = all
      //console.log('on load', displayAll(all))
      return updateState(state)
    }
    case 'change': {
      if (!action.data) {
        console.error('No data associated to action ' + action.hash)
        return state
      }
      state = {
        ...state,
        chainId: action.data.chainId,
        account: action.data.account,
      }
      //console.log('on change', displayAll(state.all))

      return updateState(state)
    }
    case 'submit': {
      if (!action.tx) {
        console.error('No tx associated to action ' + action.hash)
        return state
      }
      state.all = addTransaction(state.all, action.tx)
      //console.log('on submit', displayAll(state.all))

      return updateState(state)
    }
    case 'complete':
      if (!action.hash) {
        console.error('No hash associated to action ' + action.type)
        return state
      }
      let all = state.all
      const tx = findByHash(all, action.hash) as SuccessfulTransaction
      if (!tx) {
        // TODO: not totally idempotent, may be looked twice on dev
        console.error('No tx associated to action ' + action.hash)
        return state
      }
      tx.status = 'success'
      tx.result = {
        end: new Date().getTime(),
        blockNumber: action.data && action.data.blockNumber,
      }
      tx.data = { ...tx.data, ...action.data }
      // replacing the pending tx with a completed one
      all = all.filter(t => t.submitted.hash !== action.hash)
      all.push(tx)
      state.all = all
      saveStorage(all)
      // avoiding doublon technique in dev mode
      state.pops = state.pops.filter(t => t.submitted.hash !== action.hash)
      state.pops.push(tx)

      return updateState(state)
    case 'fail': {
      if (!action.hash) {
        console.error('No hash associated to action ' + action.type)
        return state
      }
      let all = state.all
      const tx = findByHash(all, action.hash) as FailedTransaction
      if (!tx) {
        // TODO: not totally idempotent, may be looked twice on dev
        console.error('No tx associated to action ' + action.hash)
        return state
      }
      tx.status = 'failed'
      tx.result = {
        end: new Date().getTime(),
      }
      tx.data = { ...tx.data, ...action.data }
      // replacing the pending tx with a failing one
      all = all.filter(t => t.submitted.hash !== action.hash)
      all.push(tx)
      state.all = all
      saveStorage(all)
      // avoiding doublon technique in dev mode
      state.pops = state.pops.filter(t => t.submitted.hash !== action.hash)
      state.pops.push(tx)

      return updateState(state)
    }

    case 'clear': {
      clearStorage()
      return INITIAL_TRANSACTION_STATE()
    }

    case 'pop': {
      // for test only, as it is done at the end of complete/fail
      const hash = action.hash!
      const tx = state.all.find(p => p.submitted.hash !== hash)
      return {
        ...state,
        pops: tx ? [...state.pops, tx] : state.pops,
      }
    }
    case 'unpop': {
      const hash = action.hash!
      return {
        ...state,
        pops: state.pops.filter(p => p.submitted.hash !== hash),
      }
    }
  }
}
